import { useEffect, useState } from 'react'
import { hasAuthParams } from 'react-oidc-context'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { Alert, Box, Button, Card, Divider, Grid, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import useLoginStyles from '../../components/auth/index.styles'
import LoggingIn from '../../components/auth/logging-in'
import LoggingOut from '../../components/auth/logging-out'
import Redirecting from '../../components/auth/redirecting'
import LoginFooter from '../../components/login-footer'
import loginWaves from '../../images/login-waves.png'
import meshifyCentralLogo from '../../images/meshify-central-logo.svg'
import { useAuthentication } from '../../providers/auth-provider'
import Api from '../../utils/api'
import { LOCAL_STORAGE_KEYS } from '../../utils/constants'
import { READ_PERMISSIONS_LIST } from '../../utils/constants/permissions'
import { checkIsRecentlyLoggedOut } from '../../utils/helpers'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'
import useSharedStyles from '../../utils/shared.styles'

interface UserState {
  redirect_path?: string
}

const Login = () => {
  const classes = useLoginStyles
  const sharedClasses = useSharedStyles
  const location = useLocation()
  const theme = useTheme()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const { auth, handleLogout, ownPermissions } = useAuthentication()

  const [isRecentlyLoggedOut, setIsRecentlyLoggedOut] = useState<boolean>(false)
  const [hasCheckedPermissions, setHasCheckedPermissions] = useState<boolean>(false)

  useDocumentTitle('Meshify Central')

  // Check on mount if user has recently logged out
  useEffect(() => {
    const newIsRecentlyLoggedOut = checkIsRecentlyLoggedOut()
    setIsRecentlyLoggedOut(newIsRecentlyLoggedOut)
  }, [])

  // Check for OAuth errors in URL params
  useEffect(() => {
    if (hasAuthParams()) {
      const params = new URLSearchParams(window.location.search)
      const error = params.get('error')

      // Handle OAuth errors by clearing them and showing login screen
      if (error) {
        // Clear the error params from URL
        window.history.replaceState({}, document.title, window.location.pathname)

        // If it's an interaction_required error, user needs to log in again
        if (error === 'interaction_required') {
          auth.removeUser()
        }
      }
    }
  }, [auth])

  // Automatically redirect to meshify auth if user is not authenticated and has not just logged out
  useEffect(() => {
    const signIn = async () => {
      await auth.signinRedirect({ state: { redirect_path: location.state?.from }, login_hint: 'email-first' })
    }

    if (auth && !hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !isRecentlyLoggedOut) {
      signIn()
    }
  }, [auth, auth.isAuthenticated, isRecentlyLoggedOut, location.state?.from])

  const checkPermissionsAndRedirect = async () => {
    if (!auth.user?.access_token || hasCheckedPermissions) return
    setHasCheckedPermissions(true) // Prevent multiple calls

    // If the user has no permissions, we need to check if they have the central app permission
    const resPermissions = await Api.get('/api/permissions?onlyGranted=true')
    if (resPermissions.ok) {
      const userPermissions: IPermissionType[] = resPermissions?.body
      const hasCentralAppPermission = userPermissions?.find((permission) => permission.name === READ_PERMISSIONS_LIST.central.name)

      if (hasCentralAppPermission) {
        // Check for redirect path in this order: state, sessionStorage, default dashboard
        const storedRedirect = sessionStorage.getItem('redirectAfterLogin')
        const redirectPath = (auth.user?.state as UserState)?.redirect_path ?? storedRedirect ?? '/dashboard'
        // Clear the stored redirect after using it
        if (storedRedirect) {
          sessionStorage.removeItem('redirectAfterLogin')
        }
        navigate(redirectPath)
        return
      }
      enqueueSnackbar('Missing access rights to application', { variant: 'error', preventDuplicate: true })
      await handleLogout()
      localStorage.setItem(LOCAL_STORAGE_KEYS.LOGGED_OUT, 'true')
    } else {
      // handle case if permission endpoint errored out and logout
      enqueueSnackbar('Could not verify your permissions. Please try again later', { variant: 'error', preventDuplicate: true })
      await handleLogout()
    }
  }

  if (hasAuthParams()) return <LoggingIn />

  if (auth.isAuthenticated && auth.user?.access_token && ownPermissions.find((permission) => permission.name === READ_PERMISSIONS_LIST.central.name)) {
    // Check for redirect path in this order: state, sessionStorage, default dashboard
    const storedRedirect = sessionStorage.getItem('redirectAfterLogin')
    const redirectPath = (auth.user?.state as UserState)?.redirect_path ?? storedRedirect ?? '/dashboard'

    // Only clear sessionStorage if we're actually using the stored redirect
    if (storedRedirect) {
      sessionStorage.removeItem('redirectAfterLogin')
    }

    return <Navigate to={redirectPath} replace />
  }
  if (auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator && !!auth.user?.access_token && ownPermissions.length === 0 && !hasCheckedPermissions) {
    // Only check permissions if we don't already have them loaded and haven't checked yet
    checkPermissionsAndRedirect()
    return <LoggingIn />
  }

  if (!auth.isAuthenticated && auth.activeNavigator === 'signoutRedirect') return <LoggingOut />

  if (((isRecentlyLoggedOut && !auth.isAuthenticated) || !!auth.error?.message) && !auth.isLoading && !auth.activeNavigator)
    return (
      <>
        <img src={loginWaves} alt="" style={classes.loginWaves} />
        <Grid container={true} sx={[sharedClasses.centerContent, classes.loginContainer]}>
          <Grid>
            <Card sx={classes.loginCard}>
              <img src={meshifyCentralLogo} alt="logo" style={classes.logo} />

              {auth.error?.message && (
                <Alert severity="error" icon={false}>
                  {auth.error.message}
                </Alert>
              )}

              <Box sx={[sharedClasses.fullWidth, { maxWidth: '344px', mx: 'auto' }]}>
                <Button
                  variant="contained"
                  fullWidth={true}
                  sx={{ ...classes.loginBtn, backgroundColor: theme.palette.primary.dark }}
                  disabled={auth.activeNavigator === 'signinSilent' || auth.activeNavigator === 'signoutRedirect'}
                  onClick={() => {
                    // Include sessionStorage redirect if no location state
                    const redirectPath = location.state?.from ?? sessionStorage.getItem('redirectAfterLogin')
                    auth.signinRedirect({ state: { redirect_path: redirectPath }, login_hint: 'email-first' })
                  }}
                >
                  Log in with password
                </Button>
                <Divider sx={classes.divider} textAlign="center">
                  <Typography variant="overline" style={{ color: theme.palette.text.disabled }}>
                    or
                  </Typography>
                </Divider>
                <Button
                  variant="text"
                  fullWidth={true}
                  sx={{ ...classes.loginBtn, color: theme.palette.primary.dark }}
                  disabled={auth.activeNavigator === 'signinSilent' || auth.activeNavigator === 'signoutRedirect'}
                  onClick={() => {
                    // Include sessionStorage redirect if no location state
                    const redirectPath = location.state?.from ?? sessionStorage.getItem('redirectAfterLogin')
                    auth.signinRedirect({ state: { redirect_path: redirectPath }, login_hint: 'email-otp' })
                  }}
                >
                  Send me a code instead
                </Button>
              </Box>
            </Card>
          </Grid>

          <LoginFooter />
        </Grid>
      </>
    )

  return <Redirecting />
}

export default Login
